/**
 * Попередження про незбережені зміни.
 *
 * Довго не було ніде: жодного `beforeunload`, жодного блокувальника
 * навігації. Найдорожчі місця — калькуляція, техдоки, матриця прав і норми:
 * там людина заповнює форму по кілька хвилин, а один випадковий Ctrl+W або
 * F5 стирає все без питання.
 *
 * МЕЖА, яку варто знати. Тут лише `beforeunload` — він ловить закриття
 * вкладки, перезавантаження і вихід із сайту, тобто саме ті випадки, де
 * робота зникає безповоротно. Перехід ПО САЙТУ (клік у меню) він не ловить:
 * для цього потрібен `useBlocker`, а він працює лише з data-роутером
 * (`createBrowserRouter`), у нас же звичайний `BrowserRouter`. Переїзд на
 * data-роутер — окрема робота з ризиком для всіх екранів, і робити її
 * заразом із цим не варто.
 *
 * Вікна (модалки) закриті іншим способом: вони й далі питають підтвердження
 * у власному обробнику закриття — див. `EditOrderModal.closeFromBackdrop`.
 *
 * Текст повідомлення задає браузер, не ми: свій рядок Chrome ігнорує з
 * 2016 року. Тому нема сенсу вигадувати формулювання.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useOptionalConfirm } from "@/components/ui/ConfirmDialog";

// Скільки екранів зараз тримають незбережене — сторож версії не
// перезавантажує сторінку, поки тут не нуль (Danyil 15.10.2026: «щоб не
// видалило посеред роботи»).
let unsavedHolders = 0;
export const hasUnsaved = (): boolean => unsavedHolders > 0;

export function useUnsavedWarning(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    unsavedHolders += 1;
    const release = () => { unsavedHolders -= 1; };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // `preventDefault` — сучасний спосіб; `returnValue` лишається заради
      // старих Safari/Firefox, які без нього діалог не показують.
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      release();
    };
  }, [dirty]);
}

/**
 * Те саме, але й для переходів ПО САЙТУ (клік у меню, «назад» у браузері).
 *
 * Власник 04.09: «попередження, якщо змінив і хоче переключитись — питання,
 * чи точно зберігати». `beforeunload` вище цього не ловить: він спрацьовує
 * лише на закритті вкладки й F5. Тобто найчастіший спосіб втратити роботу —
 * просто клікнути пункт меню — досі був не прикритий.
 *
 * Чому не `useBlocker`: він працює лише з data-роутером
 * (`createBrowserRouter`), а в нас звичайний `BrowserRouter`. Переїзд —
 * окрема робота з ризиком для всіх екранів.
 *
 * Тому перехоплюємо КЛІК по посиланню у фазі захоплення. Межі навмисно вузькі,
 * щоб не зламати навігацію решти застосунку: працює лише поки є незбережене,
 * лише на звичайний лівий клік без модифікаторів, лише на посилання того
 * самого походження й на інший шлях. Усе інше йде як ішло.
 *
 * `popstate` (кнопка «назад») скасувати не можна — браузер уже змінив адресу.
 * Тому повертаємо крок назад і питаємо; якщо людина погодилась піти —
 * ідемо туди, куди вона хотіла.
 */
export function useUnsavedNavigationGuard(
  dirty: boolean,
  ask: () => Promise<boolean>,
): void {
  const navigate = useNavigate();
  // Через ref, щоб не перепідписуватись на кожну літеру у формі.
  const askRef = useRef(ask);
  askRef.current = ask;

  useEffect(() => {
    if (!dirty) return;

    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.("a[href]");
      if (!(a instanceof HTMLAnchorElement)) return;
      if (a.target && a.target !== "_self") return;
      if (a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname + url.search === window.location.pathname + window.location.search) return;

      e.preventDefault();
      e.stopPropagation();
      void askRef.current().then((ok) => {
        if (ok) navigate(url.pathname + url.search);
      });
    };

    const onPop = () => {
      // Адресу вже змінено — вертаємо її назад і питаємо.
      history.pushState(null, "", window.location.href);
      void askRef.current().then((ok) => {
        if (ok) history.back();
      });
    };

    document.addEventListener("click", onClick, true);
    history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPop);
    };
  }, [dirty, navigate]);
}

/**
 * Обидва попередження одним викликом — для СТОРІНКИ з формою (не вікна:
 * вікна питають самі, див. `useGuardedClose`). Правило Danyil 12.10.2026:
 * пішов із незбереженим — система питає, а не мовчки губить.
 */
export function useUnsavedGuard(dirty: boolean): void {
  const confirm = useOptionalConfirm();
  useUnsavedWarning(dirty);
  useUnsavedNavigationGuard(dirty, async () =>
    !confirm || confirm({
      title: "Піти без збереження?",
      body: "Зміни на цьому екрані не збережені й зникнуть.",
      confirmLabel: "Піти, не зберігати",
      cancelLabel: "Лишитись",
      danger: true,
    }));
}
